import { useEffect, useState } from 'react';
import Avatar from './Avatar.jsx';
import LinkedText from './LinkedText.jsx';
import AudioNote from './AudioNote.jsx';
import PostMenu from './PostMenu.jsx';
import ProfileLinks from './ProfileLinks.jsx';
import { api } from '../api.js';
import { mediaUrl } from '../lib/upload.js';
import { MODE_LABEL, sinceLabel, whenLabel } from '../lib/format.js';

/**
 * Everything an actor has published, in full. The card previews the one thing
 * most worth knowing; this is where the rest lives, and the only surface that
 * reads a post at full size.
 *
 * An overlay rather than a replacement for the map, so presence, the room and
 * the socket all keep running underneath.
 */
export default function ActorFeed({
  handle, refreshKey = 0, me, liveActor, onClose, onShowOnGlobe, onEditProfile, onMessage,
  onEditPost, onBlock, onWave, relationOf,
}) {
  const [state, setState] = useState({ status: 'loading', actor: null, posts: [], doings: [], links: [] });
  const [reported, setReported] = useState(new Set());
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', actor: null, posts: [], doings: [], links: [] });
    api.actor(handle)
      .then((data) => live && setState({ status: 'ready', ...data }))
      .catch((err) => live && setState({ status: 'error', error: err.message, posts: [], doings: [], links: [] }));
    return () => { live = false; };
  }, [handle, refreshKey]);

  const { status, actor: fetched, posts, doings, links } = state;

  // The fetch happens once, so a face changed while this page is open — your
  // own, or someone else's arriving over the socket — would sit here stale
  // until a reload. Identity comes from the live copy when there is one; the
  // posts stay exactly as fetched.
  const actor = fetched && liveActor && liveActor.id === fetched.id
    ? {
      ...fetched,
      displayName: liveActor.displayName ?? fetched.displayName,
      seed: liveActor.seed ?? fetched.seed,
      color: liveActor.color ?? fetched.color,
      bio: liveActor.bio ?? fetched.bio,
      interests: liveActor.interests ?? fetched.interests,
    }
    : fetched;

  /** Swap an updated post into the feed without refetching it. */
  const replace = (promise) => promise
    .then(({ post }) => {
      setActionError('');
      setState((prev) => ({
        ...prev, posts: prev.posts.map((p) => (p.id === post.id ? post : p)),
      }));
    })
    .catch((error) => setActionError(error.message));
  const isSelf = !!me && actor?.id === me.id;
  // Asked of the live social state rather than the fetched profile, so waving
  // swaps the button for its receipt without refetching the feed.
  const relation = actor && !isSelf ? relationOf?.(actor) ?? 'none' : 'none';

  return (
    <div className="feed-view">
      <header className="feed-head">
        <button type="button" className="btn btn-sm" onClick={onClose}>← Back to the map</button>
      </header>

      <div className="feed-body">
        {status === 'loading' && <p className="empty-note">Loading…</p>}
        {status === 'error' && <p className="empty-note">{state.error}</p>}

        {actor && (
          <>
            <div className="feed-actor">
              <Avatar person={actor} size="lg" />
              <div className="feed-ident">
                <div className="feed-name-row">
                  <h1 className="feed-name">{actor.displayName}</h1>
                  {/* Beside the name, the way it sits beside it on the map card. */}
                  {me && !isSelf && relation === 'none' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-wave"
                      onClick={() => onWave?.(actor)}
                      title={`Say hi to ${actor.displayName}`}
                      aria-label={`Say hi to ${actor.displayName}`}
                    >
                      <span className="wave-hand" aria-hidden="true">👋</span> Say hi
                    </button>
                  )}
                </div>
                <div className="feed-handle">@{actor.username}</div>
                {actor.bio && <p className="feed-bio"><LinkedText text={actor.bio} /></p>}
                {actor.interests.length > 0 && (
                  <div className="interest-row">
                    {actor.interests.map((interest) => (
                      <span key={interest} className="interest-chip">{interest}</span>
                    ))}
                  </div>
                )}
                {doings?.[0] && (
                  <p className="feed-doing">
                    Now · {doings[0].tag}{doings[0].placeName ? ` at ${doings[0].placeName}` : ''}
                  </p>
                )}
                <ProfileLinks links={links} />
                <div className="feed-actions">
                {/* Editing belongs where the thing being edited is. It used to
                    sit behind your own face in the top bar, which is a door
                    people reasonably expect to lead to their profile. */}
                {isSelf && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={onEditProfile}>
                    Edit profile
                  </button>
                )}
                {!isSelf && (
                  <>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => onMessage?.(actor)}>
                    Message
                  </button>
                  {me && relation === 'sent' && (
                    <button type="button" className="btn btn-sm" disabled>Request sent</button>
                  )}
                  {me && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        if (!window.confirm(`Block ${actor.displayName}? You will no longer see or hear from each other.`)) return;
                        onBlock?.(actor);
                      }}
                    >
                      Block
                    </button>
                  )}
                  </>
                )}
                </div>
              </div>
              {/* Where they are standing. Top right of the block it is about. */}
              <div className="feed-tools">
                  <button
                    type="button"
                    className="icon-btn feed-tool"
                    onClick={() => onShowOnGlobe?.(actor)}
                    title={isSelf ? 'Show me on the globe' : `Show ${actor.displayName} on the globe`}
                    aria-label={isSelf ? 'Show me on the globe' : `Show ${actor.displayName} on the globe`}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 21.3c4.4-4.3 6.6-8 6.6-11.1a6.6 6.6 0 1 0-13.2 0c0 3.2 2.2 6.8 6.6 11.1z" />
                      <circle cx="12" cy="10" r="2.4" />
                    </svg>
                  </button>
              </div>
            </div>

            {posts.length === 0 && (
              <p className="empty-note">
                {isSelf ? 'Nothing posted yet.' : `${actor.displayName} hasn’t posted anything yet.`}
              </p>
            )}

            <div className="feed-posts">
              {actionError && <p className="form-error">{actionError}</p>}
              {posts.map((post) => (
                <article key={post.id} className={`feed-post ${post.status === 'gone' ? 'gone' : ''}`}>
                  {isSelf && (
                    <PostMenu
                      items={[
                        { label: 'Edit', onSelect: () => onEditPost?.(post) },

                        // Only where it does something. Currency is only
                        // extended by confirming when a post carries a mode,
                        // and a happening is decided by its start time — so
                        // offering this anywhere else would be a button that
                        // writes a timestamp and changes nothing.
                        post.status === 'live' && post.mode && post.mode !== 'happening' && {
                          label: 'Keep on the map',
                          hint: 'Confirms it is still going',
                          onSelect: () => replace(api.confirmPost(post.id)),
                        },

                        post.status === 'live' && {
                          label: 'Take off the map',
                          hint: 'Stays on your feed',
                          onSelect: () => replace(api.closePost(post.id)),
                        },

                        post.status === 'live' && post.mode === 'happening'
                          && (post.liveSince || (
                            post.startsAt > Date.now() - 12 * 60 * 60 * 1000
                            && post.startsAt < Date.now() + 2 * 60 * 60 * 1000
                          )) && {
                          label: post.liveSince ? 'End live' : 'Go live',
                          hint: post.liveSince ? 'Stops the green live state' : 'Marks this as happening now',
                          onSelect: () => replace(api.setPostLive(post.id, !post.liveSince)),
                        },

                        post.status === 'gone'
                          && (post.mode !== 'happening' || post.startsAt > Date.now()) && {
                          label: 'Put back on the map',
                          onSelect: () => replace(api.reopenPost(post.id)),
                        },

                        {
                          label: 'Delete',
                          hint: 'Cannot be undone',
                          danger: true,
                          onSelect: () => {
                            // The only irreversible one here, so it is the
                            // only one that asks.
                            if (!window.confirm('Delete this post? This cannot be undone.')) return;
                            api.deletePost(post.id).then(() => setState((prev) => ({
                              ...prev, posts: prev.posts.filter((p) => p.id !== post.id),
                            })));
                          },
                        },
                      ]}
                    />
                  )}
                  {post.body && <p className="feed-post-body"><LinkedText text={post.body} /></p>}

                  {post.media.length > 0 && (
                    <div className="feed-media">
                      {post.media.map((item) => (item.kind === 'image' ? (
                        <img
                          key={item.id}
                          src={mediaUrl(item.id)}
                          alt=""
                          loading="lazy"
                          width={item.w ?? undefined}
                          height={item.h ?? undefined}
                        />
                      ) : (
                        <AudioNote
                          key={item.id}
                          src={mediaUrl(item.id)}
                          peaks={item.peaks}
                          durationMs={item.durationMs}
                        />
                      )))}
                    </div>
                  )}

                  <div className="feed-post-meta">
                    {post.mode && <span className="mode-tag">{MODE_LABEL[post.mode]}</span>}
                    {post.mode === 'happening' && post.startsAt && (
                      <span>{whenLabel(post.startsAt)}</span>
                    )}
                    <span>{sinceLabel(post.createdAt)}</span>
                    {post.city && <span>{post.city}</span>}
                    {post.status === 'gone' && <span>no longer going</span>}
                  </div>

                  {post.tags.length > 0 && (
                    <div className="interest-row">
                      {post.tags.map((tag) => (
                        <span key={tag} className="interest-chip">{tag}</span>
                      ))}
                    </div>
                  )}

                  {!isSelf && (
                    <div className="btn-row">
                      {post.status === 'live' && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => onMessage?.(actor, post)}
                        >
                          Message about this
                        </button>
                      )}
                      {me && (
                        <button
                          type="button" className="btn btn-sm" disabled={reported.has(post.id)}
                          onClick={() => {
                            const reason = window.prompt('What should moderators know? You may leave this blank.');
                            if (reason === null) return;
                            api.reportPost(post.id, reason).then(() => setReported((current) => new Set(current).add(post.id)));
                          }}
                        >
                          {reported.has(post.id) ? 'Reported' : 'Report'}
                        </button>
                      )}
                    </div>
                  )}

                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
